import type { EntityState } from '../../lib/api.js';
import { useDevices } from '../../context/DevicesContext.js';
import { Button } from '../ui/Button.js';
import { Slider } from '../ui/Slider.js';
import { friendlyName } from '../../lib/utils.js';
import { useToast } from '../ui/Toast.js';

interface Props {
  entityId: string;
  entity: EntityState;
}

export function MediaPlayerCard({ entityId, entity }: Props) {
  const { sendCommand, patchEntity } = useDevices();
  const toast = useToast();
  const title =
    typeof entity.attributes['media_title'] === 'string'
      ? (entity.attributes['media_title'] as string)
      : '';
  const volume =
    typeof entity.attributes['volume_level'] === 'number'
      ? Math.round((entity.attributes['volume_level'] as number) * 100)
      : 0;
  const playing = entity.state === 'playing';

  const send = async (service: string, params?: Record<string, unknown>) => {
    try {
      await sendCommand('media_player', service, entityId, params);
    } catch {
      toast.show('Command failed', 'error');
    }
  };

  const handleVolume = async (v: number) => {
    const prev = entity;
    patchEntity(entityId, {
      ...entity,
      attributes: { ...entity.attributes, volume_level: v / 100 },
    });
    try {
      await sendCommand('media_player', 'volume_set', entityId, { volume_level: v / 100 });
    } catch {
      patchEntity(entityId, prev);
      toast.show('Command failed', 'error');
    }
  };

  return (
    <div
      className="p-4 rounded-xl border flex flex-col gap-2"
      style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
    >
      <div>
        <div className="font-medium text-[var(--ac-text)]">
          {friendlyName(entityId, entity.attributes)}
        </div>
        <div className="text-xs text-[var(--ac-text-muted)] truncate">{title || entity.state}</div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => send(playing ? 'media_pause' : 'media_play')}
        >
          {playing ? 'Pause' : 'Play'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => send('media_next_track')}>
          Next
        </Button>
      </div>
      <Slider value={volume} min={0} max={100} onChange={handleVolume} label="Volume" />
    </div>
  );
}
